import { AnimatePresence, motion } from 'framer-motion';
import { AlertTriangle, Download, Megaphone, MessageSquare, Plus, RefreshCw, Search, SlidersHorizontal, Trash2, Wallet, X } from 'lucide-react';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { MdCheckBox, MdCheckBoxOutlineBlank, MdIndeterminateCheckBox } from 'react-icons/md';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import CampaignPreviewModal from '../../Components/Communications/Campaigns/CampaignPreviewModal';
import CreateCampaignPopup from '../../Components/Communications/Campaigns/CreateCampaignPopup';
import { BASE_URL_API } from '../../References/URLs';
import ActiveStoreContext from '../../store/ActiveStoreContext';
import AuthContext from '../../store/auth-context';
import { deriveSendState } from '../../Components/Communications/Campaigns/shared/campaignStatus';
import styles from './ComAllCampaigns.module.css';
import '../../Components/Communications/Campaigns/shared/campaignConsole.css';
import { useNavigate } from 'react-router';

const ComAllCampaigns = () => {
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = (() => {
        try { return (asCtx?.activeStore && asCtx.activeStore.trim() !== "") ? JSON.parse(asCtx.activeStore) : null }
        catch { return null }
    })()

    const nav = useNavigate()
    const [campaignsData, setCampaignsData] = useState([])
    const [isPopUpOpen, setIsPopUpOpen] = useState(false)
    const [searchQuery, setSearchQuery] = useState('')
    const [sortBy, setSortBy] = useState('createdOn')
    const [sortOrder, setSortOrder] = useState('desc')
    const [loading, setLoading] = useState(true)
    const [currentPage, setCurrentPage] = useState(1)
    const [rowsPerPage, setRowsPerPage] = useState(10)
    const [filters, setFilters] = useState({ status: 'all', template: 'all', dateRange: 'all' })
    const [selectedCampaign, setSelectedCampaign] = useState(null)
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [selected, setSelected] = useState(new Set())
    const [isFilterSheetOpen, setIsFilterSheetOpen] = useState(false)

    // This page has its own fixed bottom elements on mobile (Create Campaign
    // FAB, bulk toolbar) — the hamburger FAB moves to top-left instead of
    // hiding entirely, since this page's own header buttons live top-right.
    useEffect(() => {
        window.dispatchEvent(new CustomEvent('layout:fixedBottomBar', { detail: { active: true } }))
        return () => {
            window.dispatchEvent(new CustomEvent('layout:fixedBottomBar', { detail: { active: false } }))
        }
    }, [])

    const campaignCreationHandler = (k) => {
        toast.success('Campaign successfully created!')
        nav(`/communications/campaign/${k}/details`)
    }

    const getAllCampaigns = async () => {
        setLoading(true)
        try {
            const res = await fetch(BASE_URL_API + "getAllCampaigns", {
                method: "POST",
                body: JSON.stringify({ token: authCtx.token, storeIdentifier: activeStore?.storeIdentifier })
            })
            if (!res.ok) throw new Error('Server error')
            const data = await res.json()
            if (data?.status === "success") {
                setCampaignsData(data?.response || [])
            } else if (data?.message === "tokenExpired") {
                authCtx.logout()
            } else {
                setCampaignsData([])
            }
        } catch (e) {
            console.error(e)
            setCampaignsData([])
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => { getAllCampaigns() }, [activeStore?.storeIdentifier])

    const normalizedCampaigns = useMemo(() => {
        const toStatus = (c) => {
            if (c?.hasDeleted === '1' || c?.hasDeleted === 1) return 'Deleted'
            if (c?.isActive === '1' || c?.isActive === 1 || c?.status === 'Active') return 'Active'
            if (c?.isActive === '0' || c?.isActive === 0 || c?.status === 'Inactive') return 'Inactive'
            return ''
        }
        return campaignsData.map((c, index) => {
            const id = [c?.campaignIdentifier, c?.ID, c?.id, c?.identifier].find(v => typeof v === 'string' && v.trim() !== '') || `campaign-${index}`
            const status = toStatus(c)
            const sendState = deriveSendState({ holdStatus: c?.holdStatus, scheduledAt: c?.scheduledAt, broadcastJobId: c?.broadcastJobId })
            return {
                id,
                campaignIdentifier: id,
                name: c?.campaignName || c?.name || 'Untitled Campaign',
                description: c?.campaignDescription || c?.description || '',
                createdOn: c?.createdOn || c?.created_at || c?.createdDate || c?.creationDate || '',
                // Opaque internal account ID, not a display name — no
                // user-friendly resolution exists for it today, so it's
                // kept around for raw/debug use but never rendered as-is.
                createdBy: c?.createdBy || c?.created_by || '',
                templateName: c?.templateName || c?.template || '',
                status,
                statusKey: status.toLowerCase(),
                templateIdentifier: c?.templateIdentifier || '',
                storeIdentifier: c?.storeIdentifier || '',
                audienceIdentifier: c?.audienceIdentifier || '',
                audienceSize: typeof c?.audienceSize === 'number' ? c.audienceSize : null,
                // delivered/read/failed are deliberately not part of
                // getAllCampaigns (only available live via
                // getBroadcastCampaignStatus) — left undefined here so the
                // card's delivery-progress section stays hidden.
                delivered: typeof c?.delivered === 'number' ? c.delivered : null,
                read: typeof c?.read === 'number' ? c.read : null,
                failed: typeof c?.failed === 'number' ? c.failed : null,
                // scheduledAt is UTC from the API — keep it raw here, only
                // convert to IST at render time via formatUTCToIST.
                scheduledAt: c?.scheduledAt || null,
                holdStatus: c?.holdStatus || null,
                broadcastJobId: c?.broadcastJobId || null,
                scheduledJobId: c?.scheduledJobId || null,
                sendState,
                raw: c,
            }
        })
    }, [campaignsData])

    const templateOptions = useMemo(() => {
        const unique = new Set()
        normalizedCampaigns.forEach(c => { if (c.templateName?.trim()) unique.add(c.templateName.trim()) })
        return Array.from(unique).sort((a, b) => a.localeCompare(b))
    }, [normalizedCampaigns])

    const processedCampaigns = useMemo(() => {
        const norm = (v) => (v ?? '').toString().toLowerCase().trim()
        const search = norm(searchQuery.trim())
        let data = [...normalizedCampaigns]

        if (search) {
            data = data.filter(c =>
                [c.name, c.description, c.createdOn, c.createdBy, c.templateName, c.campaignIdentifier].some(f => norm(f).includes(search))
            )
        }

        if (filters.status !== 'all') data = data.filter(c => c.statusKey === filters.status)
        if (filters.template !== 'all') data = data.filter(c => norm(c.templateName) === norm(filters.template))

        if (filters.dateRange !== 'all') {
            const now = new Date()
            const boundary = new Date()
            if (filters.dateRange === '7d') boundary.setDate(now.getDate() - 7)
            else if (filters.dateRange === '30d') boundary.setDate(now.getDate() - 30)
            data = data.filter(c => {
                if (!c.createdOn) return false
                const d = new Date(c.createdOn.replace(' ', 'T'))
                return !isNaN(d.getTime()) && d >= boundary && d <= now
            })
        }

        const parseDate = (v) => { const p = Date.parse((v || '').replace(' ', 'T')); return isNaN(p) ? 0 : p }
        const mul = sortOrder === 'asc' ? 1 : -1
        data.sort((a, b) => {
            if (sortBy === 'name') return mul * norm(a.name).localeCompare(norm(b.name))
            if (sortBy === 'status') {
                const rank = { active: 0, inactive: 1, deleted: 2, '': 3 }
                return mul * ((rank[a.statusKey] ?? 3) - (rank[b.statusKey] ?? 3))
            }
            if (sortBy === 'templateName') return mul * norm(a.templateName).localeCompare(norm(b.templateName))
            return mul * (parseDate(a.createdOn) - parseDate(b.createdOn))
        })
        return data
    }, [normalizedCampaigns, filters, searchQuery, sortBy, sortOrder])

    const paginatedData = useMemo(() =>
        processedCampaigns.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage),
        [processedCampaigns, currentPage, rowsPerPage]
    )

    useEffect(() => {
        const total = Math.max(1, Math.ceil(processedCampaigns.length / rowsPerPage))
        if (currentPage > total) setCurrentPage(1)
    }, [processedCampaigns, rowsPerPage, currentPage])

    const allOnPageSelected = paginatedData.length > 0 && paginatedData.every(c => selected.has(c.id))
    const someOnPageSelected = paginatedData.some(c => selected.has(c.id)) && !allOnPageSelected

    const handleOpenPreview = (campaign) => {
        if (!campaign) return
        setSelectedCampaign(campaign.raw ? { ...campaign.raw, ...campaign } : campaign)
        setIsModalOpen(true)
    }

    const handleClosePreview = () => { setSelectedCampaign(null); setIsModalOpen(false) }

    const handleSearch = (e) => { setSearchQuery(e.target.value); setCurrentPage(1) }

    const handleSort = (field) => {
        setSortOrder(sortBy === field && sortOrder === 'asc' ? 'desc' : 'asc')
        setSortBy(field)
        setCurrentPage(1)
    }

    const handlePageChange = (page) => setCurrentPage(page)

    const toggleSelect = (id) => {
        setSelected(prev => {
            const s = new Set(prev)
            if (s.has(id)) s.delete(id); else s.add(id)
            return s
        })
    }

    const selectAllOnPage = () => {
        const ids = paginatedData.map(c => c.id)
        setSelected(prev => {
            const s = new Set(prev)
            if (ids.every(id => s.has(id))) ids.forEach(id => s.delete(id))
            else ids.forEach(id => s.add(id))
            return s
        })
    }

    const bulkToggleActive = (makeActive) => {
        if (selected.size === 0) { toast.info('Select campaigns first'); return }
        if (!window.confirm(makeActive ? 'Activate selected campaigns?' : 'Deactivate selected campaigns?')) return
        setCampaignsData(prev => prev.map(c => {
            const id = c.campaignIdentifier || c.ID || c.id || c.identifier
            return selected.has(id) ? { ...c, isActive: makeActive ? '1' : '0' } : c
        }))
        toast.success('Updated selected campaigns')
        setSelected(new Set())
    }

    const bulkDelete = () => {
        if (selected.size === 0) { toast.info('Select campaigns first'); return }
        if (!window.confirm('Delete selected campaigns? This cannot be undone.')) return
        setCampaignsData(prev => prev.filter(c => !selected.has(c.campaignIdentifier || c.ID || c.id || c.identifier)))
        setSelected(new Set())
        toast.success('Deleted selected campaigns')
    }

    const handleRowsPerPageChange = (e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1) }

    const handleFilterChange = (key, value) => { setFilters(prev => ({ ...prev, [key]: value })); setCurrentPage(1) }

    const exportToCSV = () => {
        if (processedCampaigns.length === 0) { toast.info('No campaigns to export yet'); return }
        const headers = ['Campaign Name', 'Created On', 'Status', 'Send Status', 'Template Name', 'Audience Size', 'Description']
        const rows = processedCampaigns.map(c => [c.name, c.createdOn, c.status, c.sendState?.label, c.templateName, c.audienceSize, c.description].map(v => v ?? ''))
        const csv = [headers, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n')
        const a = Object.assign(document.createElement('a'), { href: URL.createObjectURL(new Blob([csv], { type: 'text/csv' })), download: 'campaigns.csv' })
        a.click()
    }

    // Delivery-progress block on each card only renders once audienceSize/
    // delivered actually come back as numbers from getAllCampaigns (see the
    // normalization above) — returns null otherwise so the card degrades
    // gracefully to just name/status/template until that data exists.
    const getDeliveryProgress = (campaign) => {
        if (!campaign.audienceSize || campaign.delivered == null) return null
        const pct = Math.min(100, Math.round((campaign.delivered / campaign.audienceSize) * 100))
        const remaining = Math.max(0, campaign.audienceSize - campaign.delivered)
        const barColor = campaign.failed > 0 && pct < 100 ? 'var(--cc-warn, #b45309)' : 'var(--cc-good, #15803d)'
        return { pct, remaining, barColor }
    }

    const totalPages = Math.max(1, Math.ceil(processedCampaigns.length / rowsPerPage))
    const activeFilterCount = ['status', 'template', 'dateRange'].filter(k => filters[k] !== 'all').length

    // Rendered twice — inline in the desktop control bar and inside the
    // mobile filter sheet — same state/handlers either way, so selecting a
    // status/template/date applies immediately in both places.
    const renderFilterSelects = () => (
        <>
            <select className={styles.filterSelect} value={filters.status} onChange={e => handleFilterChange('status', e.target.value)}>
                <option value="all">All statuses</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="deleted">Deleted</option>
            </select>
            <select className={styles.filterSelect} value={filters.template} onChange={e => handleFilterChange('template', e.target.value)}>
                <option value="all">All templates</option>
                {templateOptions.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <select className={styles.filterSelect} value={filters.dateRange} onChange={e => handleFilterChange('dateRange', e.target.value)}>
                <option value="all">All dates</option>
                <option value="7d">Last 7 days</option>
                <option value="30d">Last 30 days</option>
            </select>
        </>
    )

    return (
        <div className={`${styles.mainWrapper} ccScope`}>
            <CampaignPreviewModal isOpen={isModalOpen} onClose={handleClosePreview} campaign={selectedCampaign} />
            {isPopUpOpen && <CreateCampaignPopup isOpen={isPopUpOpen} onClose={() => setIsPopUpOpen(false)} onSuccess={campaignCreationHandler} />}

            {/* ── Page Header ── */}
            <div className={styles.pageHeader}>
                <div className={styles.pageHeaderLeft}>
                    <div className={styles.statusPill}>Communications</div>
                    <h1 className={styles.pageTitle}>Campaigns</h1>
                    <p className={styles.pageSubtitle}>Manage campaigns — quick actions, insights, and easy controls</p>
                </div>
                <div className={styles.pageHeaderRight}>
                    <div className={styles.metaBadge}>
                        {loading ? 'Loading…' : `${processedCampaigns.length} campaign${processedCampaigns.length !== 1 ? 's' : ''}`}
                    </div>
                    <div className={styles.actionBtnGroup}>
                        <button className={styles.secondaryBtn} onClick={() => nav('/communications/store-wallet')} title="Wallet transactions">
                            <Wallet size={15} /> <span className={styles.btnLabel}>Wallet</span>
                        </button>
                        <button
                            className={styles.secondaryBtn}
                            onClick={exportToCSV}
                            disabled={processedCampaigns.length === 0}
                            title={processedCampaigns.length === 0 ? 'No campaigns to export yet' : 'Export CSV'}
                        >
                            <Download size={15} /> <span className={styles.btnLabel}>Export</span>
                        </button>
                        <button className={styles.secondaryBtn} onClick={getAllCampaigns} title="Refresh">
                            <RefreshCw size={15} /> <span className={styles.btnLabel}>Refresh</span>
                        </button>
                        <button className={`${styles.primaryBtn} ${styles.hideOnMobile}`} onClick={() => setIsPopUpOpen(true)}>
                            <Plus size={15} /> Create Campaign
                        </button>
                    </div>
                </div>
            </div>

            {/* ── Control Bar ── */}
            <div className={styles.controlBar}>
                <div className={styles.searchBox}>
                    <Search size={15} className={styles.searchIcon} />
                    <input
                        className={styles.searchInput}
                        placeholder="Search campaigns, templates, creators…"
                        value={searchQuery}
                        onChange={handleSearch}
                    />
                    {searchQuery && (
                        <button className={styles.clearSearch} onClick={() => { setSearchQuery(''); setCurrentPage(1) }}>
                            <X size={14} />
                        </button>
                    )}
                </div>
                <button className={styles.filterTrigger} onClick={() => setIsFilterSheetOpen(true)} type="button">
                    <SlidersHorizontal size={14} /> Filter &amp; Sort
                    {activeFilterCount > 0 && <span className={styles.filterTriggerBadge}>{activeFilterCount}</span>}
                </button>
                <div className={styles.desktopFilters}>
                    {renderFilterSelects()}
                </div>
            </div>

            {/* ── Mobile filter sheet ── */}
            {isFilterSheetOpen && <div className={styles.filterSheetBackdrop} onClick={() => setIsFilterSheetOpen(false)} />}
            <div className={`${styles.filterSheet} ${isFilterSheetOpen ? styles.filterSheetOpen : ''}`}>
                <div className={styles.filterSheetHandle} />
                <div className={styles.filterSheetHeader}>
                    <span className={styles.filterSheetTitle}>Filter &amp; Sort</span>
                    <button className={styles.filterSheetClose} onClick={() => setIsFilterSheetOpen(false)} type="button">
                        <X size={16} />
                    </button>
                </div>
                <div className={styles.filterSheetBody}>
                    <span className={styles.filterSheetSectionLabel}>Sort by</span>
                    <div className={styles.filterSheetSortRow}>
                        {['name', 'createdOn', 'status'].map(field => (
                            <button
                                key={field}
                                type="button"
                                className={`${styles.sortBtn} ${sortBy === field ? styles.sortBtnActive : ''}`}
                                onClick={() => handleSort(field)}
                            >
                                {field === 'createdOn' ? 'Date' : field.charAt(0).toUpperCase() + field.slice(1)}
                                {sortBy === field ? (sortOrder === 'asc' ? ' ↑' : ' ↓') : ''}
                            </button>
                        ))}
                    </div>
                    <span className={styles.filterSheetSectionLabel}>Filter by</span>
                    {renderFilterSelects()}
                </div>
                <div className={styles.filterSheetFooter}>
                    <button
                        className={styles.filterSheetResetBtn}
                        onClick={() => setFilters({ status: 'all', template: 'all', dateRange: 'all' })}
                        type="button"
                    >
                        Reset
                    </button>
                    <button className={styles.filterSheetDoneBtn} onClick={() => setIsFilterSheetOpen(false)} type="button">
                        Show {processedCampaigns.length} campaign{processedCampaigns.length !== 1 ? 's' : ''}
                    </button>
                </div>
            </div>

            {/* ── Actions Row ── */}
            {/* Select/sort/rows-per-page/pagination only make sense once there's
                at least one row to act on — hidden otherwise instead of showing
                controls with nothing behind them. */}
            {!loading && processedCampaigns.length > 0 && (
                <div className={styles.actionsRow}>
                    <div className={styles.actionsLeft}>
                        <div className={styles.selectAllBtn} onClick={selectAllOnPage}>
                            {allOnPageSelected
                                ? <MdCheckBox size={18} color="#6d28d9" />
                                : someOnPageSelected
                                    ? <MdIndeterminateCheckBox size={18} color="#6d28d9" />
                                    : <MdCheckBoxOutlineBlank size={18} color="#94a3b8" />
                            }
                            <span className={styles.btnLabel}>Select visible</span>
                        </div>
                        {/* Duplicated inside the mobile filter sheet (Sort by) — hidden
                            here on mobile so this row doesn't fight the sheet's copy. */}
                        <div className={styles.inlineSortGroup}>
                            {['name', 'createdOn', 'status'].map(field => (
                                <button key={field} className={`${styles.sortBtn} ${sortBy === field ? styles.sortBtnActive : ''}`} onClick={() => handleSort(field)}>
                                    {field === 'createdOn' ? 'Date' : field.charAt(0).toUpperCase() + field.slice(1)}
                                    {sortBy === field ? (sortOrder === 'asc' ? ' ↑' : ' ↓') : ''}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className={styles.actionsRight}>
                        <div className={styles.rowsPerPage}>
                            <span>Rows</span>
                            <select className={styles.rowsSelect} value={rowsPerPage} onChange={handleRowsPerPageChange}>
                                <option value={5}>5</option>
                                <option value={10}>10</option>
                                <option value={15}>15</option>
                                <option value={50}>50</option>
                            </select>
                        </div>
                        {totalPages > 1 && (
                            <div className={styles.pagination}>
                                {totalPages > 5 ? (
                                    <>
                                        <button
                                            className={styles.navBtn}
                                            onClick={() => handlePageChange(currentPage - 1)}
                                            disabled={currentPage === 1}
                                            title="Previous page"
                                        >←</button>
                                        {currentPage > 1 && (
                                            <button className={styles.pageButton} onClick={() => handlePageChange(currentPage - 1)}>
                                                {currentPage - 1}
                                            </button>
                                        )}
                                        <button className={`${styles.pageButton} ${styles.activePage}`}>
                                            {currentPage}
                                        </button>
                                        {currentPage < totalPages && (
                                            <button className={styles.pageButton} onClick={() => handlePageChange(currentPage + 1)}>
                                                {currentPage + 1}
                                            </button>
                                        )}
                                        <button
                                            className={styles.navBtn}
                                            onClick={() => handlePageChange(currentPage + 1)}
                                            disabled={currentPage === totalPages}
                                            title="Next page"
                                        >→</button>
                                    </>
                                ) : (
                                    Array.from({ length: totalPages }, (_, i) => (
                                        <button
                                            key={i}
                                            className={`${styles.pageButton} ${currentPage === i + 1 ? styles.activePage : ''}`}
                                            onClick={() => handlePageChange(i + 1)}
                                        >{i + 1}</button>
                                    ))
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* ── Content ── */}
            {loading ? (
                <div className={styles.stateWrapper}>
                    <div className={styles.spinnerRing} />
                    <p>Loading campaigns…</p>
                </div>
            ) : paginatedData.length === 0 ? (
                <motion.div className={styles.emptyState} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
                    <Megaphone size={44} className={styles.emptyIcon} />
                    <h3>No campaigns found</h3>
                    <p>{searchQuery || filters.status !== 'all' || filters.template !== 'all'
                        ? 'Try adjusting your filters or search term.'
                        : 'Start by creating your first campaign.'
                    }</p>
                    {!searchQuery && filters.status === 'all' && filters.template === 'all' && (
                        <button className={styles.primaryBtn} onClick={() => setIsPopUpOpen(true)}>
                            <Plus size={15} /> Create Your First Campaign
                        </button>
                    )}
                </motion.div>
            ) : (
                <div className={styles.grid}>
                    {paginatedData.map((campaign, index) => {
                        const progress = getDeliveryProgress(campaign)
                        const statusChipClass = campaign.statusKey === 'active' ? 'ccChipGood'
                            : campaign.statusKey === 'deleted' ? 'ccChipDanger'
                                : 'ccChipNeutral'

                        return (
                            <motion.div
                                key={campaign.id}
                                className={`${styles.card} ${selected.has(campaign.id) ? styles.cardSelected : ''}`}
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: Math.min(index * 0.04, 0.4) }}
                            >
                                <div className={styles.cardTop}>
                                    <div className={styles.cardCheck} onClick={() => toggleSelect(campaign.id)}>
                                        {selected.has(campaign.id)
                                            ? <MdCheckBox size={16} color="#6d28d9" />
                                            : <MdCheckBoxOutlineBlank size={16} color="#cbd5e1" />
                                        }
                                    </div>
                                    <span className={`ccChip ${statusChipClass}`}>
                                        <span className="ccChipDot" />
                                        {campaign.status || '—'}
                                    </span>
                                    <span className={styles.cardDate}>
                                        {campaign.createdOn ? campaign.createdOn.split(' ')[0] : '—'}
                                    </span>
                                </div>

                                <div className={styles.cardName}>{campaign.name}</div>

                                <div className={styles.cardMetaLine}>
                                    <MessageSquare size={12} />
                                    {campaign.templateName || 'No template selected'}
                                </div>

                                <div className={styles.cardDesc}>
                                    {campaign.description
                                        ? (campaign.description.length > 110 ? campaign.description.slice(0, 110) + '…' : campaign.description)
                                        : <em className={styles.noDesc}>No description</em>
                                    }
                                </div>

                                {progress && (
                                    <div className={styles.progressSection}>
                                        <div className={styles.progressHeadRow}>
                                            <div className={styles.progressBig}>
                                                {campaign.delivered.toLocaleString()}
                                                <span className={styles.progressOf}> / {campaign.audienceSize.toLocaleString()}</span>
                                            </div>
                                            <span className="ccChip ccChipAccent">{progress.pct}%</span>
                                        </div>
                                        <div className={styles.progressTrack}>
                                            <div className={styles.progressFill} style={{ width: `${progress.pct}%`, background: progress.barColor }} />
                                        </div>
                                        <div className={styles.progressCaption}>
                                            {progress.remaining > 0 ? `${progress.remaining.toLocaleString()} remaining` : 'Delivery complete'}
                                        </div>
                                    </div>
                                )}

                                <div className={styles.cardFooter}>
                                    <div className={styles.footerItem}>
                                        <span className={styles.footerLabel}>Audience</span>
                                        <span className={styles.footerValue}>{campaign.audienceSize != null ? campaign.audienceSize.toLocaleString() : '—'}</span>
                                    </div>
                                    <div className={styles.footerDivider} />
                                    <div className={styles.footerItem}>
                                        <span className={styles.footerLabel}>Send status</span>
                                        <span className={`${styles.footerValue} ${campaign.sendState?.key === 'cancelled' ? styles.footerValueWarn : ''}`}>
                                            {campaign.sendState?.key === 'cancelled' && <AlertTriangle size={12} />}
                                            {campaign.sendState?.label || '—'}
                                        </span>
                                    </div>
                                </div>

                                <Link to={`/communications/campaign/${campaign?.campaignIdentifier}/details`} className={`ccBtn ccBtnSecondary ${styles.viewCampaignBtn}`}>
                                    View campaign
                                </Link>
                            </motion.div>
                        )
                    })}
                </div>
            )}

            {/* ── Mobile Create Campaign FAB ── */}
            {/* Mutually exclusive with the bulk toolbar below — both float
                bottom-center, so only one is ever on screen at a time. */}
            {selected.size === 0 && (
                <button className={styles.mobileFab} onClick={() => setIsPopUpOpen(true)} type="button">
                    <Plus size={16} /> Create Campaign
                </button>
            )}

            {/* ── Floating Bulk Toolbar ── */}
            <AnimatePresence>
                {selected.size > 0 && (
                    <motion.div
                        className={styles.bulkToolbar}
                        initial={{ y: 80, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: 80, opacity: 0 }}
                        transition={{ type: 'spring', stiffness: 320, damping: 28 }}
                    >
                        <div className={styles.bulkLeft}>
                            <span className={styles.bulkCount}>{selected.size} selected</span>
                        </div>
                        <div className={styles.bulkActions}>
                            <button className={styles.bulkBtn} onClick={() => bulkToggleActive(true)}>Activate</button>
                            <button className={styles.bulkBtn} onClick={() => bulkToggleActive(false)}>Deactivate</button>
                            <button className={`${styles.bulkBtn} ${styles.bulkDeleteBtn}`} onClick={bulkDelete}>
                                <Trash2 size={13} /> Delete
                            </button>
                            <button className={styles.bulkDismissBtn} onClick={() => setSelected(new Set())}>
                                <X size={13} /> Deselect All
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    )
}

export default ComAllCampaigns
